<template>
  <div class="min-h-screen bg-slate-100 py-8 px-4 print:bg-white print:p-0 print:py-0">

    <!-- Print / actions bar — hidden when printing -->
    <div class="max-w-3xl mx-auto mb-4 flex items-center justify-between print:hidden">
      <span class="text-sm text-slate-500">Invoice #{{ inv.invoice_number }}</span>
      <div class="flex items-center gap-2">
        <button @click="copyLink"
          class="flex items-center gap-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200
                 hover:bg-slate-50 px-3 py-2 rounded-xl transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"/>
          </svg>
          {{ copied ? 'Copied!' : 'Copy Link' }}
        </button>
        <button @click="window.print()"
          class="flex items-center gap-1.5 text-sm font-semibold text-white bg-indigo-500
                 hover:bg-indigo-400 px-3 py-2 rounded-xl transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z"/>
          </svg>
          Print
        </button>
      </div>
    </div>

    <!-- Invoice document -->
    <div class="max-w-3xl mx-auto bg-white shadow-md rounded-2xl overflow-hidden print:shadow-none print:rounded-none">

      <!-- Header band -->
      <div class="px-10 pt-10 pb-8 border-b border-slate-100">
        <div class="flex items-start justify-between gap-6">

          <!-- Business info -->
          <div>
            <h1 class="text-2xl font-bold text-slate-800">{{ inv.business.name || 'Business' }}</h1>
            <div class="mt-2 space-y-0.5 text-sm text-slate-500">
              <p v-if="inv.business.address">{{ inv.business.address }}</p>
              <p v-if="inv.business.phone">{{ inv.business.phone }}</p>
              <p>{{ inv.business.email }}</p>
            </div>
          </div>

          <!-- Invoice label + type badge -->
          <div class="text-right shrink-0">
            <p class="text-3xl font-extrabold tracking-tight"
              :class="inv.action === 'sold' ? 'text-emerald-600' : 'text-blue-600'">
              INVOICE
            </p>
            <span :class="inv.action === 'sold'
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-blue-50 text-blue-700'"
              class="inline-block mt-1 px-2.5 py-0.5 rounded-lg text-xs font-bold uppercase tracking-wide">
              {{ inv.action === 'sold' ? 'Sale' : 'Purchase' }}
            </span>
          </div>

        </div>
      </div>

      <!-- Meta row: invoice number, date, bill-to -->
      <div class="px-10 py-7 grid grid-cols-3 gap-6 border-b border-slate-100">

        <!-- Bill To -->
        <div class="col-span-2">
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Bill To</p>
          <p class="font-bold text-slate-800 text-base">{{ inv.customer.name }}</p>
          <p class="text-sm text-slate-600">{{ inv.customer.shop_name }}</p>
          <div class="mt-1 text-sm text-slate-500 space-y-0.5">
            <p v-if="inv.customer.address">{{ inv.customer.address }}</p>
            <p v-if="inv.customer.city">{{ inv.customer.city }}</p>
            <p v-if="inv.customer.phone">{{ inv.customer.phone }}</p>
          </div>
        </div>

        <!-- Invoice details -->
        <div class="text-right">
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Details</p>
          <div class="space-y-1.5 text-sm">
            <div>
              <p class="text-xs text-slate-400">Invoice No.</p>
              <p class="font-bold text-slate-800">#{{ inv.invoice_number }}</p>
            </div>
            <div>
              <p class="text-xs text-slate-400">Date</p>
              <p class="font-semibold text-slate-700">{{ formatDate(inv.target_date) }}</p>
            </div>
          </div>
        </div>

      </div>

      <!-- Items table -->
      <div class="px-10 py-6">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b-2 border-slate-200">
              <th class="text-left pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">#</th>
              <th class="text-left pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Product</th>
              <th class="text-center pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Qty</th>
              <th class="text-right pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Rate</th>
              <th class="text-right pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in inv.items" :key="i"
              class="border-b border-slate-50"
              :class="i % 2 === 0 ? '' : 'bg-slate-50/50'">
              <td class="py-3 text-slate-400 text-xs">{{ i + 1 }}</td>
              <td class="py-3 font-medium text-slate-800">{{ item.product_name }}</td>
              <td class="py-3 text-center text-slate-600">{{ item.quantity }}</td>
              <td class="py-3 text-right text-slate-600">Rs {{ formatNum(item.rate) }}</td>
              <td class="py-3 text-right font-semibold text-slate-800">Rs {{ formatNum(item.total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Totals + notes -->
      <div class="px-10 pb-10">
        <div class="flex items-end justify-between gap-6">

          <div class="flex-1"></div>

          <!-- Total box -->
          <div class="shrink-0 min-w-[200px]">
            <div class="border-t-2 border-slate-200 pt-4 space-y-2">
              <div class="flex justify-between text-sm text-slate-500">
                <span>Subtotal</span>
                <span>Rs {{ formatNum(inv.total) }}</span>
              </div>
              <div class="flex justify-between font-bold text-base text-slate-800 border-t border-slate-200 pt-2 mt-1">
                <span>Total</span>
                <span :class="inv.action === 'sold' ? 'text-emerald-600' : 'text-blue-600'">
                  Rs {{ formatNum(inv.total) }}
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Footer -->
      <div class="px-10 py-4 bg-slate-50 border-t border-slate-100 text-center">
        <p class="text-xs text-slate-400">Thank you for your business</p>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const props = defineProps({ invoice: Object })
const inv   = props.invoice
const copied = ref(false)

function formatNum(n) {
  return Number(n).toLocaleString('en-PK')
}

function formatDate(d) {
  if (!d) return '—'
  const [y, m, day] = d.split('-')
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${parseInt(day)} ${months[parseInt(m) - 1]}, ${y}`
}

function copyLink() {
  navigator.clipboard.writeText(window.location.href)
  copied.value = true
  setTimeout(() => { copied.value = false }, 2000)
}
</script>

<style>
@media print {
  @page { margin: 0.75in; }
  body  { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
</style>
